import React, { useEffect } from 'react';

import Grid, { GridSize } from '@mui/material/Grid';
import { type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Translate } from 'react-localize-redux';

import Tag from '@fd/ui/atoms/Tag';

import InfoIcon from '../Tooltip/InfoIcon';
import Tooltip from '../Tooltip/Tooltip';

const useStyles = makeStyles((theme: Theme) => ({
  root: (props: Props) => ({
    padding: props.removeRootPaddings ? 0 : theme.spacing(4.75, 0, 2.5, 0),
    alignItems: props.alignItems ? props.alignItems : 'flex-start',
    flexWrap: props.noWrap ? 'nowrap' : 'wrap',
  }),
  label: ({ removeRootPaddings }: Props) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    paddingTop: removeRootPaddings ? theme.spacing(2) : 0,
  }),
  description: () => ({
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1.75),
  }),
}));

export interface FormItemLayoutProps {
  alignItems?: 'stretch' | 'center' | 'flex-end' | 'flex-start' | 'baseline';
  bottomBorder?: boolean;
  children: React.ReactNode;
  className?: string;
  description?: React.ReactNode;
  descriptionId?: TranslationId;
  label?: React.ReactNode;
  labelClass?: string;
  labelId?: TranslationId;
  mdProportion?: '4x8' | '8x4' | '10x2' | '12x12';
  noWrap?: boolean;
  paddingBottom?: string;
  removeRootPaddings?: boolean;
  showBetaTag?: boolean;
  showTooltip?: boolean;
  smProportion?: '4x8' | '8x4' | '10x2' | '12x12';
  tooltipOptions?: {
    fdKey: string;
    titleId?: TranslationId;
    messageId?: TranslationId;
    labelText?: string;
  };
}

type Props = FormItemLayoutProps;
const FormItemLayout = (props: Props) => {
  const classes = useStyles(props);
  const {
    bottomBorder,
    children,
    className = '',
    description,
    descriptionId,
    label,
    labelClass = '',
    labelId,
    mdProportion,
    paddingBottom,
    showBetaTag = false,
    showTooltip,
    smProportion,
    tooltipOptions = { fdKey: '' },
  } = props;

  const [titleProportion, dataProportion] = mdProportion
    ? mdProportion.split('x').map((m) => parseInt(m, 10))
    : [4, 8];
  const [titleProportionSm, dataProportionSm] = smProportion
    ? smProportion.split('x').map((m) => parseInt(m, 10))
    : [];
  const castToGridSize = (data) => data as unknown as GridSize;

  useEffect(() => {
    if (typeof assert === 'undefined') {
      return;
    }
    assert(!(labelId && label), 'dont use labelId and label at same time');
    assert(!(descriptionId && description), "don't use descriptionId and description at same time");
  }, [labelId, label, descriptionId, description]);

  return (
    <Grid
      container
      className={clsx(classes.root, className)}
      style={{
        paddingBottom: paddingBottom ?? '',
        borderBottom: bottomBorder ? '1px solid rgba(0,0,0,0.2)' : '',
      }}
    >
      <Grid
        item
        xs={12}
        sm={castToGridSize(titleProportionSm)}
        md={castToGridSize(titleProportion)}
      >
        <div className={classes.label}>
          <Typography className={labelClass} color="textPrimary" component="h3" variant="subtitle1">
            {labelId ? <Translate id={labelId} /> : label || null}
          </Typography>

          {showBetaTag && <Tag label="Beta" />}

          {showTooltip && (
            <Tooltip {...tooltipOptions}>
              <div data-fd={tooltipOptions?.fdKey}>
                <InfoIcon size={13} verticalAlign="text-top" />
              </div>
            </Tooltip>
          )}
        </div>

        {descriptionId || description ? (
          <div className={classes.description}>
            <Typography variant="caption" color="textSecondary">
              {descriptionId ? <Translate id={descriptionId} /> : description || null}
            </Typography>
          </div>
        ) : null}
      </Grid>
      <Grid item xs={12} sm={castToGridSize(dataProportionSm)} md={castToGridSize(dataProportion)}>
        {children}
      </Grid>
    </Grid>
  );
};

export default FormItemLayout;
